import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch { return iso || ""; }
}

function toCsv(rows) {
  const escape = (v) => {
    const s = String(v ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = ["email", "name", "note", "created_at"];
  const lines = [header.join(",")];
  for (const r of rows) {
    lines.push(header.map((h) => escape(r[h])).join(","));
  }
  return lines.join("\n");
}

export default function AdminMakeupWaitlist() {
  const [items, setItems] = useState([]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/makeup-waitlist`, { withCredentials: true });
        setItems(res.data.items || []);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Failed to load");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter(
      (r) =>
        (r.email || "").toLowerCase().includes(needle) ||
        (r.name || "").toLowerCase().includes(needle) ||
        (r.note || "").toLowerCase().includes(needle),
    );
  }, [items, q]);

  const downloadCsv = () => {
    const csv = toCsv(filtered);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const stamp = new Date().toISOString().slice(0, 10);
    a.download = `makeup-waitlist-${stamp}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const copyAllEmails = async () => {
    const emails = filtered.map((r) => r.email).filter(Boolean).join(", ");
    if (!emails) {
      toast.info("Nothing to copy.");
      return;
    }
    try {
      await navigator.clipboard.writeText(emails);
      toast.success(`Copied ${filtered.length} email${filtered.length === 1 ? "" : "s"}.`);
    } catch {
      toast.error("Clipboard access denied. Try the CSV download instead.");
    }
  };

  return (
    <section data-testid="admin-makeup-waitlist">
      <div className="flex items-baseline justify-between flex-wrap gap-4 mb-8">
        <div>
          <h2 className="font-serif text-3xl md:text-4xl font-light tracking-tight">
            Makeup waitlist
          </h2>
          <p className="text-sm text-[#0F1F4E]/70 mt-1 max-w-xl font-light">
            Clients who asked to be notified when the makeup menu returns. The
            list is idempotent — one row per email.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <span
            data-testid="admin-waitlist-count"
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#F2EBD3] border border-[#D9CFA0] text-xs uppercase tracking-wider"
          >
            {items.length} on list
          </span>
          <button
            type="button"
            onClick={copyAllEmails}
            data-testid="admin-waitlist-copy-emails"
            disabled={!filtered.length}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs uppercase tracking-wider text-[#0F1F4E] border border-[#0F1F4E]/30 hover:bg-[#0F1F4E] hover:text-[#FAF6E8] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Mail className="w-3.5 h-3.5" /> Copy emails
          </button>
          <button
            type="button"
            onClick={downloadCsv}
            data-testid="admin-waitlist-export-csv"
            disabled={!filtered.length}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs uppercase tracking-wider text-[#FAF6E8] bg-[#0F1F4E] hover:bg-[#1B3370] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
        </div>
      </div>

      <div className="mb-5">
        <label className="block">
          <span className="sr-only">Filter waitlist</span>
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="admin-waitlist-filter"
            placeholder="Filter by email, name, or note…"
            className="w-full md:w-96 bg-white border border-[#D9CFA0] rounded-md px-4 py-2.5 text-sm outline-none focus:border-[#0F1F4E]"
          />
        </label>
      </div>

      {err && (
        <p className="text-red-600 text-sm mb-4" data-testid="admin-waitlist-error">
          {err}
        </p>
      )}

      {loading ? (
        <p className="text-[#0F1F4E]/70">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-[#0F1F4E]/70" data-testid="admin-waitlist-empty">
          No one has joined the waitlist yet.
        </p>
      ) : filtered.length === 0 ? (
        <p className="text-[#0F1F4E]/70">No entries match this filter.</p>
      ) : (
        <div className="overflow-x-auto bg-white border border-[#D9CFA0] rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-[#F2EBD3] text-[#0F1F4E]">
              <tr>
                <th className="text-left px-4 py-3 font-medium uppercase tracking-wider text-xs">Email</th>
                <th className="text-left px-4 py-3 font-medium uppercase tracking-wider text-xs">Name</th>
                <th className="text-left px-4 py-3 font-medium uppercase tracking-wider text-xs">Note</th>
                <th className="text-left px-4 py-3 font-medium uppercase tracking-wider text-xs whitespace-nowrap">Joined</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr
                  key={r.id || r.email}
                  data-testid={`waitlist-row-${r.email}`}
                  className="border-t border-[#D9CFA0]/60 align-top"
                >
                  <td className="px-4 py-3">
                    <a
                      href={`mailto:${r.email}`}
                      className="underline text-[#0F1F4E] hover:text-[#C9A65A] break-all"
                    >
                      {r.email}
                    </a>
                  </td>
                  <td className="px-4 py-3 text-[#0F1F4E]/85">{r.name || "—"}</td>
                  <td className="px-4 py-3 text-[#0F1F4E]/85 max-w-sm">
                    <span className="whitespace-pre-wrap">{r.note || "—"}</span>
                  </td>
                  <td className="px-4 py-3 text-xs text-[#0F1F4E]/60 whitespace-nowrap">
                    {fmtDate(r.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
